import {Search, Users} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import ColonyAvatar from '@/components/ColonyAvatar'
import ColonyOfficialStar from '@/components/ColonyOfficialStar'
import Layout from '@/components/Layout'
import SeoHead from '@/components/SeoHead'
import { colonyProfilePath } from '@/lib/colonyProfile'
import { isEthAddress } from '@/lib/launchpad/contracts'
import { browseWalletProfiles, colonyDisplayName, type WalletProfileSummary } from '@/lib/walletProfiles'

type SortMode = 'newest' | 'oldest'

function ExploreColony() {
  const [profiles, setProfiles] = useState<WalletProfileSummary[]>([])
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<SortMode>('newest')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)
    browseWalletProfiles({ limit: 60, sort }).then((list) => {
      if (active)
        setProfiles(list)
    }).finally(() => {
      if (active)
        setLoading(false)
    })
    return () => { active = false }
  }, [sort])

  const trimmed = query.trim().toLowerCase()
  const searchedWallet = isEthAddress(trimmed) ? trimmed : ''
  const visible = useMemo(() => {
    if (!trimmed)
      return profiles
    if (searchedWallet)
      return profiles.filter(profile => profile.wallet.toLowerCase() === searchedWallet)
    return profiles.filter((profile) => {
      const username = (profile.username || '').toLowerCase()
      return username.includes(trimmed)
    })
  }, [profiles, searchedWallet, trimmed])

  return (
    <Layout>
      <SeoHead
        title="Explore the Colony"
        description="Discover Penguin Pad Colony usernames, follow wallets, and browse public identity on Robinhood Chain."
        url="https://penguinpad.xyz/explore-colony"
      />

      <section className="mx-auto max-w-6xl px-4 pb-16 pt-12 sm:px-6 lg:px-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">Community</p>
        <h1 className="mt-2 text-3xl font-bold text-white sm:text-4xl">Explore the Colony</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-400">
          Public Colony profiles with usernames and photos. Search by username — wallets stay hidden.{' '}
          <Link to="/colony-newsfeed" className="font-medium text-lime-400 hover:text-lime-300">Open the Colony Newsfeed</Link>
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <label className="block flex-1">
            <span className="sr-only">Search username</span>
            <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 backdrop-blur-md">
              <Search className="h-4 w-4 text-zinc-500" aria-hidden="true" />
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Search username"
                className="w-full bg-transparent text-sm text-white placeholder:text-zinc-500 focus:outline-none"
              />
            </div>
          </label>
          <div className="flex shrink-0 rounded-2xl border border-white/10 bg-white/[0.03] p-1">
            {(['newest', 'oldest'] as const).map(option => (
              <button
                key={option}
                type="button"
                onClick={() => setSort(option)}
                className={`rounded-xl px-3 py-2 text-xs font-semibold capitalize ${
                  sort === option ? 'bg-lime-400 text-black' : 'text-zinc-400 hover:text-white'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {searchedWallet && visible.length === 0
          ? (
              <Link
                to={colonyProfilePath(searchedWallet)}
                className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-lime-400/30 bg-lime-400/10 px-4 py-3 text-sm font-medium text-lime-300"
              >
                Open this Colony profile
              </Link>
            )
          : null}

        {loading
          ? <p className="mt-8 text-sm text-zinc-500">Loading Colony members…</p>
          : visible.length > 0
            ? (
                <ul className="mt-8 grid grid-cols-3 gap-1 sm:grid-cols-4 sm:gap-2 md:grid-cols-5 lg:grid-cols-6">
                  {visible.map((profile) => {
                    const name = colonyDisplayName(profile.wallet, profile.username)
                    return (
                      <li key={profile.wallet}>
                        <Link
                          to={colonyProfilePath(profile.wallet)}
                          className="group flex flex-col items-center gap-1.5 rounded-xl p-1.5 text-center hover:bg-white/[0.04] sm:p-2"
                        >
                          <span className="relative aspect-square w-full overflow-hidden rounded-xl bg-white/[0.03] ring-1 ring-white/10 group-hover:ring-lime-400/40">
                            <ColonyAvatar wallet={profile.wallet} image={profile.image} size="full" anonymous />
                          </span>
                          <span className="flex min-w-0 max-w-full items-center gap-1">
                            <span className="truncate text-[11px] font-medium text-zinc-200 group-hover:text-white sm:text-xs">{name}</span>
                            <ColonyOfficialStar wallet={profile.wallet} size="sm" />
                          </span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              )
            : (
                <div className="mt-10 rounded-3xl border border-dashed border-white/15 px-4 py-12 text-center">
                  <Users className="mx-auto h-7 w-7 text-zinc-600" aria-hidden="true" />
                  <p className="mt-3 text-sm text-zinc-400">
                    {trimmed
                      ? 'No matching usernames yet.'
                      : 'No public Colony profiles yet. Set a username or photo on My Colony to appear here.'}
                  </p>
                </div>
              )}
      </section>
    </Layout>
  )
}

export default ExploreColony
